import { camelizeKeys } from 'humps';
import { normalize } from 'normalizr';
import {
  JobInterface,
  WorksOrdersInterface,
  Order,
  WorksReportsInterface,
  Inspection,
  InspectionInterface,
  Job,
  ReportRecord,
  Works,
} from '../../types';
import { JobsSchema } from './schema';

export const normalizeJobsIntoWorksInterface = (
  data: any,
  captureException: (message: string, data: any) => void,
): Works | undefined => {
  const result = normalize(camelizeKeys({ jobs: data }), JobsSchema);

  const {
    jobs,
    orders,
    orderStakeholders,
    orderUnits,
    properties,
    permits,
    jobInspections: inspections,
    scheduledProposals: inspectionProposals,
    reports,
  } = result.entities;

  if (
    !jobs
    || !orders
    || !properties
    || !inspections
    || !inspectionProposals
  ) {
    captureException(
      'Jobs normalized doesn\'t have all required entities:',
      {
        source: 'normalizeJobsIntoWorksInterface',
        graphqlResponse: Object.keys(data).join(', '),
        normalizedData: Object.keys(result.entities).join(', '),
      },
    );
    return undefined;
  }

  const jobsInterface = Object.keys(jobs).reduce((acc: JobInterface, id) => {
    if (!jobs[id]) return acc;

    const job: Job = {
      id,
      orderId: jobs[id].order,
      inspectionIds: jobs[id].inspections,
      reportIds: jobs[id].reports,
      status: jobs[id].status,
      scheduledTime: jobs[id].scheduledTime,
      isFee: jobs[id].isFee,
      isGoAnytime: jobs[id].isGoAnytime,
      isMock: jobs[id].isMock,
    };

    return { ...acc, [id]: job };
  }, {});

  return {
    jobs: jobsInterface,
    bundleOffers: {},
    orders: Object.keys(orders).reduce((acc: WorksOrdersInterface, id) => {
      if (!orders[id]) return acc;
      const order: Order = {
        id,
        propertyId: orders[id].propertyId,
        inspectionIds: orders[id].inspections,
        orderStakeholderIds: orders[id].orderStakeholders,
        status: orders[id].status,
        teamId: orders[id].teamId,
        team: orders[id].team,
        readOnlyToken: orders[id].readOnlyToken,
        createdAt: orders[id].createdAt,
        contingencyDate: orders[id].contingencyDate,
        earliestInspectionDate: orders[id].earliestInspectionDate,
        isMock: orders[id].isMock,
        isAppointmentBased: orders[id].isAppointmentBased,
        inspectionSchedulingDeadline: orders[id].inspectionSchedulingDeadline,
        accessInformation: orders[id].accessInformation,
        orderUnitIds: orders[id].orderUnits,
        accessInformations: orders[id].accessInformations,
        inspectorInstructions: orders[id].inspectorInstructions,
        troubleshootingInfo: orders[id].team.automatedTroubleshootingInformation,
        useCustomDeficiencyBank: orders[id].team.useCustomDeficiencyBank,
        useCustomLimitationBank: orders[id].team.useCustomLimitationBank,
        allowPublishReportBeforeCompletion: orders[id].team.allowPublishReportBeforeCompletion,
        occupancyInfo: orders[id].occupancyInfo,
        isPaywall: orders[id].isPaywall,
        teamExternalId: orders[id].teamExternalId,
        communityName: orders[id].communityName,
        floorPlanName: orders[id].floorPlanName,
      };

      return { ...acc, [id]: order };
    }, {}),
    inspections: Object.keys(inspections).reduce((acc: InspectionInterface, id) => {
      if (!inspections[id]) return acc;

      const inspection: Inspection = {
        id,
        orderId: inspections[id].orderId,
        status: inspections[id].status,
        inspectionType: inspections[id].inspectionType,
        inspectionSubtype: inspections[id].inspectionSubtype,
        scheduledProposalId: inspections[id].scheduledProposalId,
        createdAt: inspections[id].createdAt,
        templateReportId: inspections[id].templateReportId,
        geotaggingRequired: inspections[id].geotaggingRequired,
        appRequirement: inspections[id].appRequirement,
        customerFacingName: inspections[id].customerFacingName,
      };

      return { ...acc, [inspection.id]: inspection };
    }, {}),
    reports: Object.keys(reports || {}).reduce((acc: WorksReportsInterface, id) => {
      if (!reports?.[id]) return acc;

      const job = Object.values(jobsInterface).find(({ reportIds }) => reportIds?.includes(id));

      if (!job) captureException('Job not found for report', { reportId: id });

      const cannotAnswerNoAccessRequiresMedia = (
        orders[job?.orderId || '']?.team?.cannotAnswerNoAccessRequiresMedia || false
      );

      const reportRecord: ReportRecord = {
        id,
        inspectionIds: job?.inspectionIds || [],
        reportSystemIds: reports[id].reportSystems,
        templateReportIds: reports[id].templateReportIds,
        originalReportId: reports[id].originalReportId,
        templateNames: reports[id].templateNames,
        pdfDocumentUrl: reports[id].pdfDocumentUrl,
        noDeficienciesAllowed: reports[id].noDeficienciesAllowed,
        noLimitationsAllowed: reports[id].noLimitationsAllowed,
        autoDeficiencyLocationRequired: reports[id].autoDeficiencyLocationRequired,
        geotaggingRequired: reports[id].geotaggingRequired,
        cannotAnswerNoAccessRequiresMedia,
        includePermitData: reports[id].includePermitData,
        includeWideAnglePhotos: reports[id].includeWideAnglePhotos,
        createdAt: reports[id].createdAt,
        updatedAt: reports[id].updatedAt,
        startedAt: reports[id].startedAt,
        publishedAt: reports[id].publishedAt,
        complianceText: reports[id].complianceText,
      };

      return { ...acc, [reportRecord.id]: reportRecord };
    }, {}),
    inspectionProposals,
    properties: Object.keys(properties || {}).reduce((acc, id) => {
      const { permits: propertyPermits, ...rest } = properties[id];

      const propertyRecord = {
        ...rest,
        permitIds: propertyPermits,
      };

      return { ...acc, [id]: propertyRecord };
    }, {}),
    permits: permits || {},
    orderStakeholders: orderStakeholders || {},
    orderUnits,
    lastFilters: [],
  };
};

export * from './schema';
